<template>
	<div
		id="transport"
		ref="transport_ref"
		class="transport col-12 px-0 d-none d-md-flex my-4 font-body font-size-16 font-weight-default"
	>
		<div class="w-100">
			<div class="title-container d-flex justify-content-between">
				<h3 class="title-4 text-grey-800 mb-2">
					{{ "1. " }}{{ store.state.content.planner_optForPackages}}
				</h3>
			</div>

			<div class="tabs-wrapper d-flex flex-row nowrap mt-4">
				<div
					v-for="(option, option_i) in options"
					@click.stop="handleSwitch(option.value)"
					class="tab col"
					:class="
						option.selected
							? 'active font-weight-700'
							: ''
					"
					:key="option_i"
				>
					<div class="tab-inner-wrapper">
						<div class="d-flex justify-content-end align-items-center">
							<RadioCircle
								:active="option.selected"
								:colorDefault="colors.darkBlue"
								:colorActive="'white'"
								:additionalClasses="'me-1 me-xl-3'"
							/>
						</div>
						<span class="d-flex justify-content-center align-items-center">
							{{ option.label }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, onMounted } from "vue"
import RadioCircle from "@/vue/common/radioCircle.vue"
import availableNonTransportPackageCodes from "@/vue/planning/computed/availableNonTransportPackageCodes"
import colors from "@/config/colors"
import { useStore } from "vuex"
import flightPackCode from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import { Transport } from "@/types"

const store = useStore();

class SwitchOption {
	label: string
	value: Transport | null
	selected: boolean
	constructor(label: string, value: Transport | null, selected: boolean) {
		this.label = label
		this.value = value
		this.selected = selected
	}
}

const selectedTransport = computed<Transport | null>(() => store.getters.transportv2)

const isFlightEnabled = computed(() =>
	store.getters.availablePackCodes.some((pack: { codePackage: string }) => flightPackCode.includes(pack.codePackage))
)
const isTrainEnabled = computed(() =>
	store.getters.availablePackCodes.some((pack: { codePackage: string }) => trainPackCode.includes(pack.codePackage))
)

const getDefaultPackCodeForTransport = (transport: Transport | null): string | undefined => {
	if (transport === Transport.FLIGHT) {
		return store.getters.availableFlightPackCodes[0]?.codePackage
	}
	if (transport === Transport.TRAIN) {
		return store.getters.availableTrainPackCodes[0]?.codePackage
	}
	const nonTransportPacks = store.getters.availableNonTransportPackCodes
	if (store.state.season === "E") {
		const seasonPacks = nonTransportPacks.filter(
			(pack: { codePackage: string }) =>
				pack.codePackage === "HS" || pack.codePackage === "PMA"
		)
		return seasonPacks[0]?.codePackage ?? nonTransportPacks[0]?.codePackage
	}
	return nonTransportPacks[0]?.codePackage
}

const options: ComputedRef<SwitchOption[]> = computed(() => {
	const { content } = store.state
	const switchOptions: SwitchOption[] = []
	const hasBothTransports = isFlightEnabled.value && isTrainEnabled.value

	if (isFlightEnabled.value) {
		switchOptions.push(
			new SwitchOption(
				hasBothTransports
					? (content.transport_flight || content.planner_formulaWithFlight)
					: (content.planner_formulaWithFlight || content.transport_flight),
				Transport.FLIGHT,
				selectedTransport.value === Transport.FLIGHT
			)
		)
	}

	if (isTrainEnabled.value) {
		switchOptions.push(
			new SwitchOption(
				hasBothTransports
					? (content.transport_train || content.planner_formulaWithTransport)
					: content.planner_formulaWithTransport,
				Transport.TRAIN,
				selectedTransport.value === Transport.TRAIN
			)
		)
	}

	if (availableNonTransportPackageCodes.value && availableNonTransportPackageCodes.value.length) {
		switchOptions.push(
			new SwitchOption(
				content.planner_noTransport || content.transport_none,
				null,
				selectedTransport.value === null
			)
		)
	}

	return switchOptions
})

const handleSwitch = async (transport: Transport | null) => {
	if (selectedTransport.value === transport) return

	const defaultPackCode = getDefaultPackCodeForTransport(transport)
	const shouldOpenParticipantsDropdown =
		store.getters.isLocaleEN &&
		(transport === Transport.FLIGHT || transport === Transport.TRAIN) &&
		store.state.participants.length > 9

	if (shouldOpenParticipantsDropdown) {
		store.commit("setParticipants", store.state.participants.slice(0, 9))
	}

	store.commit("setTransportv2", transport)
	store.commit("setTransportTrue", transport !== null)
	store.commit("setTransportationSelected", transport !== null)

	if (transport === Transport.FLIGHT) {
		if (defaultPackCode) {
			await store.dispatch("setPackage", defaultPackCode)
		}
		await store.dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true })
	} else if (transport === Transport.TRAIN) {
		if (defaultPackCode) {
			await store.dispatch("setPackage", defaultPackCode)
		}
		await store.dispatch("selectDefaultTrainOrigin", {
			overwrite: true,
			forceTrainPackCode: true,
			packCode: defaultPackCode,
		})
	} else {
		store.commit("setFlightOrigin", null)
		if (defaultPackCode) {
			await store.dispatch("setPackage", defaultPackCode)
		}
	}

	if (shouldOpenParticipantsDropdown) {
		setTimeout(() => {
			store.dispatch("setParticipantsDropdownOpen", true)
		}, 0)
	}
}

onMounted(() => {
	if (store.getters.transportv2 != null) return

	if (flightPackCode.includes(store.state.packCode)) {
		store.commit("setTransportv2", Transport.FLIGHT)
		store.commit("setTransportTrue", true)
	} else if (trainPackCode.includes(store.state.packCode)) {
		store.commit("setTransportv2", Transport.TRAIN)
		store.commit("setTransportTrue", true)
	} else {
		store.commit("setTransportv2", null)
		store.commit("setTransportTrue", false)
	}
});
</script>

<style scoped>
.tab-inner-wrapper {
	display: grid;
	grid-template-columns: 70px 1fr 70px;
}

.tab {
	background: white;
	border-bottom: 1px solid #dee2e6;
	flex-grow: 1;
	height: 70px;
	border-radius: 0px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: color 150ms ease-in-out, box-shadow 150ms ease-in-out;
	position: relative;
}

.tab:not(.active):hover {
	background: white;
	color: var(--primary-blue);
	box-shadow: inset 0 0 0 1px var(--primary-blue);
}

.tab:not(:first-child):not(:last-child),
.tab:not(:first-child):not(:last-child):hover {
	border-radius: 0;
}
.tab:not(:first-child) {
	border-right: 1px solid #dee2e6;
}
.tab:not(:last-child) {
	border-left: 1px solid #dee2e6;
}
.tab:last-child,
.tab:first-child {
	border-left: none;
	border-right: none;
}

.tab:first-child {
	border-top-left-radius: 16px;
	border-bottom-left-radius: 16px;
	border-top-right-radius: 0px;
	border-bottom-right-radius: 0px;
}

.tab:last-child {
	border-top-right-radius: 16px;
	border-bottom-right-radius: 16px;
	border-top-left-radius: 0px;
	border-bottom-left-radius: 0px;
}

.tab.active {
	background: var(--primary-blue);
	border: none;
	color: white;
}

.tabs-wrapper {
	border-radius: var(--border-radius-secondary);
}
</style>
